import { Badge, Card, CardContent, CardHeader, CardTitle } from '@iptv/ui';
import { useQuery } from '@tanstack/react-query';

import { adminFetch } from '../lib/api';

interface OrderRow {
  id: number;
  order_ref: string;
  status: string;
  total_cents: number;
  currency: string;
  created_at: string;
}

interface SubscriptionRow {
  id: number;
  status: string;
  expires_at: string;
}

function formatPrice(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function statusBadge(status: string) {
  if (status === 'paid') return <Badge variant="success">{status}</Badge>;
  if (status === 'awaiting_payment') return <Badge variant="warning">{status}</Badge>;
  if (status === 'cancelled' || status === 'failed') return <Badge variant="destructive">{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

export function DashboardPage() {
  const orders = useQuery({
    queryKey: ['admin', 'orders', 'recent'],
    queryFn: () => adminFetch<OrderRow[]>('/api/v1/admin/orders', { query: { limit: 10 } }),
  });

  const subs = useQuery({
    queryKey: ['admin', 'subscriptions', 'recent'],
    queryFn: () =>
      adminFetch<SubscriptionRow[]>('/api/v1/admin/subscriptions', { query: { limit: 10 } }),
  });

  const today = new Date().toISOString().slice(0, 10);
  const ordersToday = orders.data?.filter((o) => o.created_at.slice(0, 10) === today) ?? [];
  const paidToday = ordersToday.filter((o) => o.status === 'paid');
  const revenueToday = paidToday.reduce((acc, o) => acc + o.total_cents, 0);

  const activeSubs = subs.data?.filter((s) => s.status === 'active').length ?? 0;
  const expiring7d = (subs.data ?? []).filter((s) => {
    const days = (new Date(s.expires_at).getTime() - Date.now()) / 86_400_000;
    return s.status === 'active' && days >= 0 && days <= 7;
  }).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard label="Orders today" value={ordersToday.length} />
        <KpiCard label="Paid today" value={paidToday.length} />
        <KpiCard label="Revenue today" value={formatPrice(revenueToday)} />
        <KpiCard label="Active subs" value={activeSubs} sublabel={`${expiring7d} expiring 7d`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent orders</CardTitle>
          </CardHeader>
          <CardContent>
            {orders.isPending ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="pb-2">Ref</th>
                    <th className="pb-2">Total</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.data?.slice(0, 8).map((o) => (
                    <tr key={o.id} className="border-t">
                      <td className="py-2 font-mono text-xs">{o.order_ref}</td>
                      <td className="py-2">{formatPrice(o.total_cents, o.currency)}</td>
                      <td className="py-2">{statusBadge(o.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            {subs.isPending ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="pb-2">ID</th>
                    <th className="pb-2">Expires</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.data?.slice(0, 8).map((s) => (
                    <tr key={s.id} className="border-t">
                      <td className="py-2">#{s.id}</td>
                      <td className="py-2 text-xs">{new Date(s.expires_at).toLocaleDateString()}</td>
                      <td className="py-2">{statusBadge(s.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sublabel }: { label: string; value: string | number; sublabel?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold">{value}</div>
        {sublabel && <div className="mt-0.5 text-xs text-muted-foreground">{sublabel}</div>}
      </CardContent>
    </Card>
  );
}
